import { useMutation } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { faChevronDown, faRefresh, faWarning } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Collapsible from '@radix-ui/react-collapsible';
import {
    createConnectionMapping,
    triggerIntegrationActions,
    updateConnectionMapping,
} from 'api/integrations';
import { BLOOMERANG_INTEGRATION_NOT_FOUND_ERROR } from 'assets/js/pages/account-settings/account-integrations/constants';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentAccountIntegrationMappings } from 'customHooks/useCurrentAccountIntegrationMappings';
import { useCurrentAccountIntegrations } from 'customHooks/useCurrentAccountIntegrations';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { Campaign, IntegrationMapping } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface BloomerangMappingItemFormValues {
    fund: string;
    campaign: string;
    appeal: string;
}

interface BloomerangMappingItemProps {
    campaign: Campaign;
}

const BloomerangMappingItem = ({ campaign }: BloomerangMappingItemProps) => {
    const { success, error } = useToast();
    const { accountId } = useCurrentAccount();
    const { data: integrations } = useCurrentAccountIntegrations();
    const { bloomerang } = integrations || {};

    const { data: optionsData } = useCurrentAccountIntegrationMappings('account');
    const options = optionsData?.bloomerang?.[0].data;
    const { data: mappingsData } = useCurrentAccountIntegrationMappings('campaign');
    const mapping = mappingsData?.bloomerang?.find(
        item => item.mappable_id.toString() === campaign.id.toString(),
    );

    const methods = Form.useGivebutterForm<BloomerangMappingItemFormValues>({
        defaultValues: {
            fund: mapping?.settings.fund?.id.toString() || '',
            campaign: mapping?.settings.campaign?.id.toString() || '',
            appeal: mapping?.settings.appeal?.id.toString() || '',
        },
    });
    const {
        control,
        formState: { isDirty, isValid },
        path,
        handleSubmit,
        reset,
    } = methods;

    const { mutate: triggerSync, isPending: isTriggeringSync } = useMutation<
        unknown,
        GivebutterAxiosError
    >({
        mutationFn: () => {
            if (!bloomerang?.id) {
                throw new Error(BLOOMERANG_INTEGRATION_NOT_FOUND_ERROR);
            }

            return triggerIntegrationActions(
                accountId,
                bloomerang.id,
                ['historical.sync'],
                campaign.id,
            );
        },
        onSuccess: () => {
            success(
                'Historical sync is in progress, please allow up to 10 minutes for this process to complete.',
            );
        },
        onError: e => {
            error(e.response?.data.message || 'Something went wrong. Please try again');
        },
    });

    const { mutate: submit, isPending: isLoading } = useMutation<
        IntegrationMapping,
        GivebutterAxiosError,
        BloomerangMappingItemFormValues
    >({
        mutationFn: values => {
            const payload = {
                id: campaign.id,
                context: 'bloomerang',
                type: 'App\\Models\\Campaign\\Campaign',
                settings: JSON.stringify({
                    fund: options?.funds.find(fund => fund.id === parseInt(values.fund)),
                    appeal: options?.appeals.find(appeal => appeal.id === parseInt(values.appeal)),
                    campaign: options?.campaigns.find(
                        campaign => campaign.id === parseInt(values.campaign),
                    ),
                }),
            };

            return mapping
                ? updateConnectionMapping(mapping?.id, payload)
                : createConnectionMapping(payload);
        },
        onSuccess: (_response, { fund, appeal, campaign }) => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.integrations(accountId),
            });
            success('Campaign Mapping settings saved successfully!');
            reset({ fund, appeal, campaign });
        },
        onError: e => {
            error(e.response?.data.message || 'Something went wrong. Please try again');
        },
    });

    return (
        <Collapsible.Root key={campaign.id}>
            <Box className="flex flex-col gap-6 p-4">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm font-medium">{campaign.title}</p>
                        {!mapping?.settings.fund && (
                            <p className="flex items-baseline gap-1 text-xs text-orange-700">
                                <FontAwesomeIcon icon={faWarning} size="sm" />
                                This campaign hasn’t been mapped yet.
                            </p>
                        )}
                    </div>
                    <Collapsible.Trigger asChild className="group">
                        <Button
                            size="sm"
                            variant="transparent"
                            shape="square"
                            aria-label="Toggle Campaign Mapping Settings"
                        >
                            <FontAwesomeIcon
                                className="group-radix-state-open:rotate-180"
                                icon={faChevronDown}
                            />
                        </Button>
                    </Collapsible.Trigger>
                </div>
                <Collapsible.Content>
                    <Form.Provider {...methods}>
                        <form
                            onSubmit={handleSubmit(values => submit(values))}
                            className="flex flex-col gap-4"
                        >
                            <div className="flex flex-col gap-2 md:flex-row">
                                <Form.Container className="min-w-0">
                                    <Form.Label name={path('fund')}>
                                        Fund <Form.LabelAsterisk />
                                    </Form.Label>
                                    <Form.SingleSelect control={control} name="fund">
                                        <SingleSelect.Trigger aria-label="Fund" />
                                        <SingleSelect.Content>
                                            <SingleSelect.Item value="none">
                                                <SingleSelect.ItemText>None</SingleSelect.ItemText>
                                            </SingleSelect.Item>
                                            {options?.funds.map(fund => (
                                                <SingleSelect.Item
                                                    value={fund.id.toString()}
                                                    key={fund.id}
                                                >
                                                    <SingleSelect.ItemText>
                                                        {fund.name}
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                            ))}
                                        </SingleSelect.Content>
                                    </Form.SingleSelect>
                                </Form.Container>
                                <Form.Container className="min-w-0">
                                    <Form.Label name={path('campaign')}>Campaign</Form.Label>
                                    <Form.SingleSelect control={control} name="campaign">
                                        <SingleSelect.Trigger aria-label="Campaign" />
                                        <SingleSelect.Content>
                                            <SingleSelect.Item value="none">
                                                <SingleSelect.ItemText>None</SingleSelect.ItemText>
                                            </SingleSelect.Item>
                                            {options?.campaigns.map(campaign => (
                                                <SingleSelect.Item
                                                    value={campaign.id.toString()}
                                                    key={campaign.id}
                                                >
                                                    <SingleSelect.ItemText>
                                                        {campaign.name}
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                            ))}
                                        </SingleSelect.Content>
                                    </Form.SingleSelect>
                                </Form.Container>
                                <Form.Container className="min-w-0">
                                    <Form.Label name={path('appeal')}>Appeal</Form.Label>
                                    <Form.SingleSelect control={control} name="appeal">
                                        <SingleSelect.Trigger aria-label="Appeal" />
                                        <SingleSelect.Content>
                                            <SingleSelect.Item value="none">
                                                <SingleSelect.ItemText>None</SingleSelect.ItemText>
                                            </SingleSelect.Item>
                                            {options?.appeals.map(appeal => (
                                                <SingleSelect.Item
                                                    value={appeal.id.toString()}
                                                    key={appeal.id}
                                                >
                                                    <SingleSelect.ItemText>
                                                        {appeal.name}
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                            ))}
                                        </SingleSelect.Content>
                                    </Form.SingleSelect>
                                </Form.Container>
                            </div>
                            <div className="flex justify-between">
                                <Form.SubmitButton
                                    size="sm"
                                    disabled={!isDirty || !isValid}
                                    isSubmitting={isLoading}
                                >
                                    Save
                                </Form.SubmitButton>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    color="neutral"
                                    onClick={() => {
                                        triggerSync();
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={faRefresh}
                                        className={isTriggeringSync ? 'animate-spin' : ''}
                                    />
                                    Run Historical Sync
                                </Button>
                            </div>
                        </form>
                    </Form.Provider>
                </Collapsible.Content>
            </Box>
        </Collapsible.Root>
    );
};

export { BloomerangMappingItem };
